import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  isAfter,
  isBefore,
  isValid,
  parse,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns';

import { DatePickerPresetGroup, DefaultPresetOptions } from './types';

export const createRelativePresets = () => {
  const now = startOfDay(new Date());
  return [
    {
      key: 'today',
      label: '오늘',
      getValue: () => ({ from: now, to: now }),
    },
    {
      key: 'yesterday',
      label: '어제',
      getValue: () => {
        const yesterday = subDays(now, 1);
        return { from: yesterday, to: yesterday };
      },
    },
    {
      key: 'thisWeek',
      label: '이번 주',
      getValue: () => ({
        from: startOfWeek(now, { weekStartsOn: 1 }),
        to: endOfWeek(now, { weekStartsOn: 1 }),
      }),
    },
    {
      key: 'lastWeek',
      label: '지난주',
      getValue: () => {
        const lastWeek = subWeeks(now, 1);
        return {
          from: startOfWeek(lastWeek, { weekStartsOn: 1 }),
          to: endOfWeek(lastWeek, { weekStartsOn: 1 }),
        };
      },
    },
    {
      key: 'thisMonth',
      label: '이번 달',
      getValue: () => ({
        from: startOfMonth(now),
        to: endOfMonth(now),
      }),
    },
    {
      key: 'lastMonth',
      label: '지난달',
      getValue: () => {
        const lastMonth = subMonths(now, 1);
        return {
          from: startOfMonth(lastMonth),
          to: endOfMonth(lastMonth),
        };
      },
    },
    {
      key: 'thisYear',
      label: '올해',
      getValue: () => ({
        from: startOfYear(now),
        to: endOfYear(now),
      }),
    },
  ];
};

export const createYearQuarterPresets = (year: number) => {
  return [
    {
      key: `${year}-full`,
      label: `${year}년 전체`,
      getValue: () => ({
        from: startOfDay(new Date(year, 0, 1)),
        to: endOfDay(new Date(year, 11, 31)),
      }),
    },
    {
      key: `${year}-h1`,
      label: '상반기',
      getValue: () => ({
        from: startOfDay(new Date(year, 0, 1)),
        to: endOfDay(new Date(year, 5, 30)),
      }),
    },
    {
      key: `${year}-h2`,
      label: '하반기',
      getValue: () => ({
        from: startOfDay(new Date(year, 6, 1)),
        to: endOfDay(new Date(year, 11, 31)),
      }),
    },
    {
      key: `${year}-q1`,
      label: '1분기',
      getValue: () => ({
        from: startOfDay(new Date(year, 0, 1)),
        to: endOfDay(new Date(year, 2, 31)),
      }),
    },
    {
      key: `${year}-q2`,
      label: '2분기',
      getValue: () => ({
        from: startOfDay(new Date(year, 3, 1)),
        to: endOfDay(new Date(year, 5, 30)),
      }),
    },
    {
      key: `${year}-q3`,
      label: '3분기',
      getValue: () => ({
        from: startOfDay(new Date(year, 6, 1)),
        to: endOfDay(new Date(year, 8, 30)),
      }),
    },
    {
      key: `${year}-q4`,
      label: '4분기',
      getValue: () => ({
        from: startOfDay(new Date(year, 9, 1)),
        to: endOfDay(new Date(year, 11, 31)),
      }),
    },
  ];
};

export const createMonthlyPresets = (year: number) => {
  return Array.from({ length: 12 }, (_, i) => ({
    key: `${year}-m${i + 1}`,
    label: `${i + 1}월`,
    getValue: () => ({
      from: startOfDay(new Date(year, i, 1)),
      to: endOfMonth(new Date(year, i, 1)),
    }),
  }));
};

// Default presets
export const createDefaultPresets = (
  year: number,
  options: DefaultPresetOptions = {},
): DatePickerPresetGroup[] => {
  const { showRelative = true, showYearQuarter = true, showMonthly = true } = options;

  const groups: DatePickerPresetGroup[] = [];

  if (showRelative) {
    groups.push({
      items: createRelativePresets(),
      divider: showYearQuarter || showMonthly,
    });
  }

  if (showYearQuarter) {
    groups.push({
      items: createYearQuarterPresets(year),
      divider: showMonthly,
    });
  }

  if (showMonthly) {
    groups.push({
      items: createMonthlyPresets(year),
    });
  }

  return groups;
};

// 숫자만 추출하고 최대 8자리까지만 허용
export const extractNumbers = (value: string): string => {
  return value.replace(/\D/g, '').slice(0, 8);
};

// 숫자 문자열을 YYYY-MM-DD 형식으로 변환
export const formatDateInput = (numbers: string): string => {
  if (numbers.length === 0) return '';
  if (numbers.length <= 4) return numbers;
  if (numbers.length <= 6) return `${numbers.slice(0, 4)}-${numbers.slice(4)}`;
  return `${numbers.slice(0, 4)}-${numbers.slice(4, 6)}-${numbers.slice(6)}`;
};

// 날짜 유효성 검증 옵션
export interface DateValidationOptions {
  minDate?: Date;
  maxDate?: Date;
  // Range 모드에서만 사용
  rangeStart?: Date;
  rangeEnd?: Date;
}

// 날짜가 유효하고 minDate/maxDate 범위 내에 있는지 확인
export const isValidDate = (date: Date, options: DateValidationOptions = {}): boolean => {
  if (!isValid(date)) return false;

  const { minDate, maxDate, rangeStart, rangeEnd } = options;

  // minDate/maxDate 범위 체크
  if (minDate && isBefore(date, minDate)) return false;
  if (maxDate && isAfter(date, maxDate)) return false;

  // Range 모드에서 시작일이 종료일보다 늦으면 안됨
  if (rangeStart && isAfter(date, rangeStart)) return false;
  if (rangeEnd && isBefore(date, rangeEnd)) return false;

  return true;
};

// 날짜 입력 핸들러 생성 (숫자만 추출하고 포맷팅)
export const createDateInputHandler = (
  setValue: (value: string) => void,
  setDate: (date: Date) => void,
) => {
  return (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const numbers = extractNumbers(inputValue);
    const formatted = formatDateInput(numbers);
    setValue(formatted);

    if (numbers.length === 8) {
      const date = parse(formatted, 'yyyy-MM-dd', new Date());
      if (isValidDate(date)) {
        setDate(date);
      }
    }
  };
};

// 날짜 키다운 핸들러 생성 (백스페이스로 하이픈 삭제 시 앞 숫자도 함께 삭제)
export const createDateKeyDownHandler = (
  currentValue: string,
  setValue: (value: string) => void,
) => {
  return (e: React.KeyboardEvent<HTMLInputElement>) => {
    // 백스페이스 키만 처리
    if (e.key !== 'Backspace') return;

    const input = e.currentTarget;
    const cursorPosition = input.selectionStart || 0;

    // 하이픈 위치에서 백스페이스를 눌렀는지 확인
    if (currentValue[cursorPosition - 1] === '-') {
      e.preventDefault();
      // 하이픈 위치에서 삭제된 경우, 앞 숫자도 삭제
      const beforeHyphen = currentValue.slice(0, cursorPosition - 2);
      const afterHyphen = currentValue.slice(cursorPosition);
      const combined = beforeHyphen + afterHyphen;
      const numbers = extractNumbers(combined);
      const formatted = formatDateInput(numbers);
      setValue(formatted);

      // 커서 위치 조정
      setTimeout(() => {
        const newCursorPosition = Math.max(0, cursorPosition - 2);
        input.setSelectionRange(newCursorPosition, newCursorPosition);
      }, 0);
    }
  };
};

// 날짜 블러 핸들러 생성
export const createDateBlurHandler = ({
  inputValue,
  onReset,
  setDate,
  validationOptions = {},
}: {
  inputValue: string;
  onReset: () => void;
  setDate: (date: Date) => void;
  validationOptions?: DateValidationOptions;
}) => {
  return () => {
    const numbers = extractNumbers(inputValue);

    // 8자리 미입력일 때 이전값 복원
    if (numbers.length < 8) {
      onReset();
      return;
    }

    // 8자리 입력시 유효성 검토
    const dateStr = formatDateInput(numbers);
    const date = parse(dateStr, 'yyyy-MM-dd', new Date());

    // 유효하지 않은 날짜이거나 비활성 날짜일 때 이전값 복원
    if (!isValidDate(date, validationOptions)) {
      onReset();
      return;
    }

    // 유효한 날짜면 업데이트
    setDate(date);
  };
};
